import React, { useEffect, useState } from "react"
import { useStateMachine } from "../../../context/stateMachine/StateMachineContext"
import { COMPANY_DEDUCTIBLE_STATUS, useUserDeductible } from "../../../context/user/DeductibleContext"
import { Steps } from "../../workflow/steps"
import { waitForMinimumDelay } from "../../../../utils/WaitForMinimumDelay"
import useInitialSetup from "../../hooks/useInitialSetup"
import { CenteredLoadingIndicator } from "../../../views/molecules/Molecules"
import { useBenefitEnrollment } from "#/components/context/enrollment/BenefitEnrollmentContext"
import { useCurrentUser } from "../../../context/user/UserContext"
import { GetWorkflow } from "../BenefitEnrollmentFlow"
import { Workflows } from "../../workflow/workflows"
import { MemberPlanSourceOptions } from "../MemberPlanSourceStep"
import { PartnerPlanSourceOptions } from "../PartnerPlanSourceStep"
import { Box } from "@carrotfertility/carotene-core"
import { reportError } from "#/utils/ErrorReporting"

const viewName = Steps.PROCESS_MEMBER_ENROLLMENT

function ProcessMemberEnrollmentStep(): JSX.Element {
  const {
    status: { needsBenefitEnrollment, areCommonInsuranceFieldsCompleted }
  } = useBenefitEnrollment()

  const {
    cmdConfiguration,
    companyDeductibleStatus,
    updateUserBenefitEnrollment,
    resetReimbursementDetailsEnrollmentStarted,
    enrollmentCreatedAndDataRefreshed
  } = useUserDeductible()
  const externallyManagedShouldSkip =
    areCommonInsuranceFieldsCompleted && companyDeductibleStatus === COMPANY_DEDUCTIBLE_STATUS.EXTERNAL

  const {
    employeeId,
    memberPlanSource,
    partnerPlanSource,
    partnerPlanSourceSelectValue,
    //all the below values are for logging purposes in the sentry thrown below in useInitialSetup
    benefitEnrollmentWizardCompleted,
    partnerCoverageType,
    partnerHasHSA,
    memberPlanSourceSelectValue,
    memberCoverageType,
    memberHasHSA,
    memberHasLegalPartner,
    memberCoverageTypeSelectValue,
    partnerCoverageTypeSelectValue,
    memberCoverageStartDate,
    memberCoverageEndDate,
    partnerCoverageStartDate,
    partnerCoverageEndDate,
    legallyMarried,
    partnerAsDependent,
    memberHasHDHP,
    memberHsaContributionStatus,
    partnerHasHDHP,
    partnerHsaContributionStatus
  } = useCurrentUser()
  const workflowType = GetWorkflow()

  const { setNextStep } = useStateMachine(
    viewName,
    !needsBenefitEnrollment || externallyManagedShouldSkip ? "ALREADY_ENROLLED" : null
  )

  const [initialSetupComplete, setInitialSetupComplete] = useState(false)
  const [startedEnrollment, setStartedEnrollment] = useState(false)
  const [showLoadingIndicator, setShowLoadingIndicator] = useState(true)

  useInitialSetup(async () => {
    if (needsBenefitEnrollment) {
      setStartedEnrollment(true)
      await waitForMinimumDelay(updateUserBenefitEnrollment(), 1000)
    } else if (!needsBenefitEnrollment && benefitEnrollmentWizardCompleted == null) {
      // Log a sentry to indicate we're in a bad state. If we don't needBenefitEnrollment
      // but benefitEnrollmentWizardCompleted is still null we won't complete the enrollment properly
      reportError(
        new Error("ProcessMemberEnrollmentStep: BenefitEnrollmentWizardCompleted is not going to be populated."),
        {
          step: "ProcessMemberEnrollmentStep",
          reason:
            "needsBenefitEnrollment was false in ProcessMemberEnrollmentStep's useInitialSetup which means BenefitEnrollmentWizardCompleted is null.",
          benefitEnrollmentWizardCompleted: benefitEnrollmentWizardCompleted,
          employeeId: employeeId?.toString(),
          legallyMarried: legallyMarried?.toString(),
          memberHasLegalPartner: memberHasLegalPartner?.toString(),
          partnerAsDependent: partnerAsDependent?.toString(),
          memberPlanSource: memberPlanSource,
          memberPlanSourceSelectValue: memberPlanSourceSelectValue,
          memberCoverageType: memberCoverageType,
          memberCoverageTypeSelectValue: memberCoverageTypeSelectValue,
          memberHasHDHP: memberHasHDHP?.toString(),
          memberHasHSA: memberHasHSA?.toString(),
          memberHsaContributionStatus: memberHsaContributionStatus,
          memberCoverageStartDate: memberCoverageStartDate,
          memberCoverageEndDate: memberCoverageEndDate,
          partnerPlanSource: partnerPlanSource,
          partnerPlanSourceSelectValue: partnerPlanSourceSelectValue,
          partnerCoverageType: partnerCoverageType,
          partnerCoverageTypeSelectValue: partnerCoverageTypeSelectValue,
          partnerHasHDHP: partnerHasHDHP?.toString(),
          partnerHasHSA: partnerHasHSA?.toString(),
          partnerHsaContributionStatus: partnerHsaContributionStatus,
          partnerCoverageStartDate: partnerCoverageStartDate,
          partnerCoverageEndDate: partnerCoverageEndDate
        }
      )
    }
    setInitialSetupComplete(true)
  })

  useEffect(() => {
    // If we started a new enrollment we need to wait for data to be refreshed before
    // setting the next step.  If we didn't need to start creation of a new enrollment, we can move
    // immediately to the next step.
    if (
      (initialSetupComplete && startedEnrollment && enrollmentCreatedAndDataRefreshed) ||
      (initialSetupComplete && !startedEnrollment)
    ) {
      resetReimbursementDetailsEnrollmentStarted()

      setShowLoadingIndicator(false)
      let nextStep

      if (!startedEnrollment && workflowType !== Workflows.ACA_ONLY) {
        // If we didn't start a new enrollment it means we already enrolled and can move on to Connect Insurance
        nextStep = "ALREADY_ENROLLED"
      } else if (
        workflowType === Workflows.ACA_ONLY &&
        (memberPlanSource === MemberPlanSourceOptions.OTHER ||
          memberPlanSource === MemberPlanSourceOptions.NOT_INSURED ||
          partnerPlanSource === PartnerPlanSourceOptions.OTHER ||
          partnerPlanSource === PartnerPlanSourceOptions.NOT_INSURED)
      ) {
        nextStep = "HRA_INELIGIBLE"
      } else if (
        workflowType === Workflows.EMD &&
        partnerPlanSourceSelectValue !== PartnerPlanSourceOptions.MEMBER &&
        partnerPlanSource !== null
      ) {
        nextStep = "PARTNER_INELIGIBLE"
      } else if (workflowType === Workflows.CMD_NON_HDHP) {
        nextStep = "CMD_NON_HDHP"
      } else {
        nextStep =
          companyDeductibleStatus === COMPANY_DEDUCTIBLE_STATUS.CARROT_MANAGED_DEDUCTIBLE
            ? cmdConfiguration
            : companyDeductibleStatus
      }
      setNextStep(Steps.PROCESS_MEMBER_ENROLLMENT, nextStep)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- See https://carrotfertility.atlassian.net/wiki/spaces/PE/pages/2050295461/Remove+Build+Warnings#react-hooks%2Fexhaustive-deps
  }, [initialSetupComplete, enrollmentCreatedAndDataRefreshed, startedEnrollment])

  return <Box padding={(theme) => theme.tokens.spacing.xs}>{showLoadingIndicator && <CenteredLoadingIndicator />}</Box>
}

export { ProcessMemberEnrollmentStep }
